import { FC, useMemo, useState } from 'react';
import Description from '../../../components/Description';
import AttachmentPopup from '../../../../../shared/AttachmentPopup';
import { QuestSchema } from '../../../../../types/quests';
import { QuestActivityType } from '../../../../../enums/quests';
import color from '../../../../../theme/color';
import {
    Container,
    AttachmentsContainer,
    MarkdownContainer,
    DescriptionText
} from './styled';
import useAppDispatch from '../../../../../hooks/useAppDispatch';

import { getQuestActivities } from '../../../../../utils/quests';
import { generateUniqueId } from '../../../../../utils/attachments';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';

import { getViewUrl } from '../../../../../redux/attachments/service';
import AttachmentItem from '../../../../../shared/AttachmentItem';
import { IAttachment } from '../../../../../types/attachments';

interface Props {
    quest: QuestSchema;
}

const DescriptionSection: FC<Props> = ({ quest }) => {
    const dispatch = useAppDispatch();

    const [popupState, setPopupState] = useState({
        isOpen: false,
        url: '',
        type: '',
        name: '',
        currentIndex: 0,
        fileId: 0 as number
    });

    const modificationActivities = useMemo(
        () =>
            getQuestActivities(
                quest,
                QuestActivityType.MODIFICATIONS_REQUESTED,
                'asc'
            ),
        [quest]
    );

    // Get the current description (latest modification or original)
    const currentDescription = useMemo(() => {
        if (modificationActivities.length > 0) {
            // If there are modifications, the latest one is the current description
            return (
                modificationActivities[modificationActivities.length - 1]
                    .details.text || ''
            );
        }
        // Otherwise, use the original quest description
        return quest.description;
    }, [modificationActivities, quest.description]);

    const currentDescriptionLabel = useMemo(() => {
        if (modificationActivities.length > 0) {
            return `Description Iteration #${modificationActivities.length}`;
        }
        return 'Description';
    }, [modificationActivities]);

    // Get previous iterations (all modifications except the last, then original)
    // Ordered from newest to oldest
    const previousIterations = useMemo(() => {
        const iterations = [];
        // Add all modification activities except the last one (in reverse order - newest first)
        if (modificationActivities.length > 0) {
            for (let i = modificationActivities.length - 1; i > 0; i--) {
                iterations.push({
                    id: modificationActivities[i].id,
                    text: modificationActivities[i].details.text || '',
                    iterationNumber: i,
                    label: `Description Iteration #${i}`
                });
            }
            // Add original description as iteration #1 (always last)
            iterations.push({
                id: 'original',
                text: quest.description,
                iterationNumber: 1,
                label: 'Original Description'
            });
        }
        return iterations;
    }, [quest.description, modificationActivities]);

    const getAttachmentUrl = async (file: IAttachment, index: number) => {
        if (!file.fileId) {
            console.error('File ID is missing:', file);
            return;
        }
        console.log('Fetching attachment URL for:', file);
        try {
            const response = await dispatch(
                getViewUrl({ fileId: file.fileId })
            ).unwrap();
            setPopupState({
                isOpen: true,
                url: response.url,
                type: file.type,
                name: file.name,
                currentIndex: index,
                fileId: file.fileId
            });
        } catch (error) {
            console.error('Error fetching attachment URL:', error);
        }
    };

    const InlineMarkdown = ({ source }: { source: string }) => (
        <span style={{ display: 'inline', verticalAlign: 'middle' }}>
            <MarkdownEditor value={source} readOnly inline />
        </span>
    );

    const processDescriptionToElements = (text: string) => {
        const regex = /\[Attachment\]\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g;
        const elements: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let index = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            const [fullMatch, fileName] = match;
            const startIndex = match.index;
            // Extract the text before the attachment and render it with MDEditor.Markdown
            const before = text.substring(lastIndex, startIndex);
            if (before.trim()) {
                elements.push(
                    <InlineMarkdown key={`md-${index}`} source={before} />
                );
            }

            const isImage = /\.(jpg|jpeg|png|gif|bmp|svg)$/i.test(fileName);
            const file: IAttachment = {
                id: generateUniqueId(), // local id for UI changes
                name: fileName,
                type: isImage ? 'image/jpeg' : 'video/mp4'
            };
            elements.push(
                <span
                    key={`attachment-${index}`}
                    style={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        margin: '0 4px'
                    }}
                >
                    <AttachmentItem
                        file={file}
                        index={index}
                        getAttachmentUrl={getAttachmentUrl}
                    />
                </span>
            );
            lastIndex = startIndex + fullMatch.length;
            index++;
        }

        const after = text.substring(lastIndex);
        if (after.trim()) {
            elements.push(<InlineMarkdown key={`md-after`} source={after} />);
        }
        return elements;
    };

    const closePopup = () => {
        setPopupState((prev) => ({ ...prev, isOpen: false }));
    };

    const handleNextAttachment = async () => {
        if (!quest.attachments || quest.attachments.length === 0) return;

        const nextIndex =
            (popupState.currentIndex + 1) % quest.attachments.length;
        const nextFile = quest.attachments[nextIndex];

        await getAttachmentUrl(nextFile, nextIndex);
    };

    return (
        <Container>
            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={closePopup}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                currentIndex={popupState.currentIndex + 1}
                totalItems={quest.attachments?.length || 0}
                onNext={handleNextAttachment}
                fileId={popupState.fileId}
            />
            <Description
                label={currentDescriptionLabel}
                description={
                    <MarkdownContainer>
                        <DescriptionText>
                            {processDescriptionToElements(currentDescription)}
                        </DescriptionText>
                    </MarkdownContainer>
                }
            />
            {quest.attachments && quest.attachments.length > 0 && (
                <AttachmentsContainer>
                    <div
                        style={{
                            color: color.lightBlack3,
                            marginBottom: 8
                        }}
                    >
                        Attachments
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            // gap: '8px',
                            maxHeight: '150px',
                            overflowY: 'auto'
                        }}
                    >
                        {quest.attachments.map((file, index) => (
                            <AttachmentItem
                                file={file}
                                index={index}
                                getAttachmentUrl={getAttachmentUrl}
                            />
                        ))}
                    </div>
                </AttachmentsContainer>
            )}

            {previousIterations.map((iteration) => {
                return (
                    <Description
                        key={iteration.id}
                        label={iteration.label}
                        description={
                            <MarkdownContainer>
                                <MarkdownEditor
                                    value={iteration.text}
                                    readOnly
                                />
                            </MarkdownContainer>
                        }
                    />
                );
            })}
        </Container>
    );
};

export default DescriptionSection;
